import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import { TableAdapter } from '@makyoapp/makyo-common'
import { Checkbox, Switch } from '@makyoapp/makyo-ui'

import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useCreateColumnContext } from '@/context/CreateColumnContext'

import { useGridV2Context } from '../../contexts/GridV2Context'
import { ColumnDd } from './ColumnDd'
import { Fld, Lbl } from './shared'
import { TableColumnDd } from './TableColumnDd'

function FieldMatchMultiple() {
  const { id, value, setValue } = useCreateColumnContext()
  const v = value.connectionOptions.connMultiple || false
  return (
    <Fld>
      <Lbl>
        <Trans>Match Multiple</Trans>
      </Lbl>
      <div className='flex-c'>
        <Switch
          id={`${id}-lookup-col-multiple`}
          checked={v}
          onClick={(connMultiple) => {
            setValue({
              connectionOptions: {
                ...value.connectionOptions,
                connMultiple,
              },
            })
          }}
        />
        <div className='help text-light'>
          {v ? <Trans>match 1 row to many target rows</Trans> : <Trans>match 1 row to 1 target row</Trans>}
        </div>
      </div>
    </Fld>
  )
}

export function FieldConnectionStepTwo() {
  const { id, value, lookupCols, setLookupCols } = useCreateColumnContext()
  const { tables } = useDbClientContext()
  const targetTable = tables.find((tb) => tb.id === value.connectionOptions.connFTableId)

  return (
    <Fld isVertical>
      <div className='mb-1 text-base'>
        <Trans>
          Add lookup columns? <span className='text-light'>(optional)</span>
        </Trans>
      </div>
      <div className='text-light mb-4'>
        <Trans i18nKey='lookupNote' values={{ targetTable: targetTable?.name }}>
          Lookup fields show data from connected columns. Select specific columns from{' '}
          <strong>{targetTable?.name}</strong> table to add them as Lookup columns in this table.
        </Trans>
      </div>
      <div>
        {targetTable?.columns.map((cl) => {
          return (
            <label key={cl.id} className='cursor-pointer flex-c py-2'>
              <div>
                <Checkbox
                  id={`${id}-lookup-col-chk-${cl.id}`}
                  inputClassName='mk-ag-lookup-col-chk'
                  checked={Boolean(lookupCols.find((lc) => lc.id === cl.id))}
                  onClick={(isChecked) => {
                    if (isChecked) {
                      const copy = [...lookupCols]
                      copy.push(cl)
                      setLookupCols(copy)
                    } else {
                      setLookupCols(lookupCols.filter((lc) => lc.id !== cl.id))
                    }
                  }}
                />
              </div>
              <div>{cl.name}</div>
            </label>
          )
        })}
      </div>
    </Fld>
  )
}

export default function FieldConnection() {
  const { id, value, setValue } = useCreateColumnContext()

  const { tableId } = useGridV2Context()
  const { tables } = useDbClientContext()
  const { t } = useTranslation()

  const options = useMemo(() => {
    return tables.filter((value: TableAdapter) => {
      return value.isDeleted === false && value.id !== tableId
    })
  }, [tableId, tables])

  return (
    <div>
      <div className='text-light pt-4 pb-2'>
        <Trans>Create a connection between this table and another table when:</Trans>
      </div>
      <Fld>
        <Lbl>
          <Trans>The value in this column</Trans>
        </Lbl>
        <ColumnDd
          id={`${id}-connection-col`}
          selected={value.connectionOptions.connColId}
          excludeTypes={[ColumnTypes.Connection, ColumnTypes.Lookup]}
          onChange={(opt) => {
            setValue({
              connectionOptions: {
                ...value.connectionOptions,
                connColId: opt.id,
              },
            })
          }}
          placeholder={t('Select a column')}
          showRowId
        />
      </Fld>
      <Fld>
        <Lbl>
          <Trans>Matches the value in this column</Trans>
        </Lbl>
        <TableColumnDd
          withSearch
          id={`${id}-conn-table`}
          value={{
            table: value.connectionOptions.connFTableId,
            column: value.connectionOptions.connFColId,
          }}
          excludeTypes={[ColumnTypes.Connection]}
          options={options}
          onChange={(tableId, colId) => {
            setValue({
              connectionOptions: {
                ...value.connectionOptions,
                connFTableId: tableId,
                connFColId: colId,
                connFColIdToShow: colId,
              },
            })
          }}
        />
      </Fld>
      <FieldMatchMultiple />
    </div>
  )
}
